// src/components/CodeLookupModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { Search, X, Plane, Building2, CheckCircle } from 'lucide-react';

const CodeLookupModal = ({ show, onHide, onSelect, fieldType }) => {
  const [activeTab, setActiveTab] = useState('airports');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [recentlySelected, setRecentlySelected] = useState(null);

  // Helper function to get JWT token for authenticated requests
  const getAuthHeaders = () => {
    const token = localStorage.getItem('jwtToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers.Authorization = `${tokenType} ${token}`;
    }
    
    return headers;
  };

  // Reset state when modal opens/closes
  useEffect(() => {
    if (show) {
      setSearchResults([]);
      setError(null);
      setSelectedItem(null);
      setRecentlySelected(null);
      
      if (fieldType === 'carrier') {
        setActiveTab('airlines');
      } else {
        setActiveTab('airports');
      }
    }
  }, [show, fieldType]);

  // Search entities using the API
  const searchEntities = async (inputValue, type) => {
    const trimmedValue = inputValue.trim();
    
    // Validate based on type
    if (type === 'airport' && trimmedValue.length < 3) {
      return [];
    }
    if (type === 'carrier' && trimmedValue.length < 2) {
      return [];
    }
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/countries/search?type=${type}&query=${encodeURIComponent(trimmedValue)}`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
        }
      );
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed');
        }
        throw new Error(`Failed to search ${type}`);
      }
      
      const data = await response.json();
      
      return data.map(item => ({
        code: item.code,
        name: item.name
      }));
    } catch (error) {
      console.error('Entity search error:', error);
      throw error;
    }
  };

  // Debounced search function
  const performSearch = useCallback(async (query, type) => {
    const trimmedQuery = query.trim();
    
    // Check minimum length based on type
    const minLength = type === 'airport' ? 3 : 2;
    if (trimmedQuery.length < minLength) {
      setSearchResults([]);
      setError(null);
      return;
    }

    setIsSearching(true);
    setError(null);
    
    try {
      const results = await searchEntities(query, type);
      setSearchResults(results);
      
      if (results.length === 0) {
        setError('No results found. Try a different search term.');
      }
    } catch (err) {
      console.error('Search error:', err);
      if (err.message === 'Authentication failed') {
        setError('Authentication failed. Please log in again.');
      } else {
        setError('Failed to search. Please try again.');
      }
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounce timer
  useEffect(() => {
    const type = activeTab === 'airports' ? 'airport' : 'carrier';
    
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setError(null);
      return;
    }

    const minLength = type === 'airport' ? 3 : 2;
    if (searchQuery.trim().length < minLength) {
      setSearchResults([]);
      setError(null);
      return;
    }

    const timer = setTimeout(() => {
      performSearch(searchQuery, type);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery, activeTab, performSearch]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const type = activeTab === 'airports' ? 'airport' : 'carrier';
      performSearch(searchQuery, type);
    }
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
    if (onSelect) {
      onSelect(item.code, item.name, activeTab);
    }
    setRecentlySelected(item.code);
    
    // Copy to clipboard
    navigator.clipboard.writeText(item.code).catch(err => {
      console.error('Failed to copy to clipboard:', err);
    });
    
    setTimeout(() => setRecentlySelected(null), 2000);
    setTimeout(() => setSelectedItem(null), 1500);
  };

  const getModalTitle = () => {
    switch (fieldType) {
      case 'origin':
        return 'Search Origin Airport';
      case 'destination':
        return 'Search Destination Airport';
      case 'carrier':
        return 'Search Airline';
      default:
        return 'Code Lookup';
    }
  };

  const getHelperText = () => {
    if (activeTab === 'airports') {
      return 'Search by airport code (e.g., DFW, ORD) or airport name (e.g., Dallas, Chicago). Enter at least 3 characters to search.';
    } else {
      return 'Search by airline code (e.g., AA, UA) or airline name (e.g., American, United). Enter at least 2 characters to search.';
    }
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1050
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        width: '90%',
        maxWidth: '700px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        {/* Modal Header */}
        <div style={{
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #dee2e6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h5 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 500 }}>{getModalTitle()}</h5>
          <button
            onClick={onHide}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '0',
              color: '#6c757d',
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
          {/* Tabs */}
          <div style={{ 
            display: 'flex', 
            borderBottom: '1px solid #dee2e6',
            marginBottom: '1rem'
          }}>
            <button
              onClick={() => {
                setActiveTab('airports');
                setSearchResults([]);
                setError(null);
                setSearchQuery('');
              }}
              style={{
                padding: '0.5rem 1rem',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                borderBottom: activeTab === 'airports' ? '2px solid #0d6efd' : '2px solid transparent',
                color: activeTab === 'airports' ? '#0d6efd' : '#6c757d',
                fontWeight: activeTab === 'airports' ? 500 : 400
              }}
            >
              <Plane size={16} />
              Airports
            </button>
            <button
              onClick={() => {
                setActiveTab('airlines');
                setSearchResults([]);
                setError(null);
                setSearchQuery('');
              }}
              style={{
                padding: '0.5rem 1rem',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                borderBottom: activeTab === 'airlines' ? '2px solid #0d6efd' : '2px solid transparent',
                color: activeTab === 'airlines' ? '#0d6efd' : '#6c757d',
                fontWeight: activeTab === 'airlines' ? 500 : 400
              }}
            >
              <Building2 size={16} />
              Airlines
            </button>
          </div>

          {/* Helper Text */}
          <p style={{ color: '#6c757d', fontSize: '0.875rem', marginBottom: '1rem' }}>
            {getHelperText()}
          </p>

          {/* Search Input */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <input
                type="text"
                placeholder={`Search ${activeTab === 'airports' ? 'airport' : 'airline'} code or name...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                autoFocus
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  paddingRight: '2.5rem',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
              />
              {isSearching && (
                <div style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '16px',
                  height: '16px',
                  border: '2px solid #0d6efd',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }} />
              )}
            </div>
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSearchResults([]);
                  setError(null);
                }}
                style={{
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  background: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Error Alert */}
          {error && (
            <div style={{
              padding: '0.75rem 1rem',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              color: '#664d03',
              marginBottom: '1rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>{error}</span>
              <button
                onClick={() => setError(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.25rem',
                  color: '#664d03'
                }}
              >
                ×
              </button>
            </div>
          )}

          {/* Success feedback */}
          {recentlySelected && (
            <div style={{
              padding: '0.75rem 1rem',
              backgroundColor: '#d1e7dd',
              border: '1px solid #0f5132',
              borderRadius: '4px',
              color: '#0f5132',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <CheckCircle size={16} />
              Code "{recentlySelected}" has been copied to clipboard
            </div>
          )}

          {/* Results */}
          {searchResults.length > 0 && (
            <div style={{
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              maxHeight: '400px',
              overflowY: 'auto'
            }}>
              {searchResults.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleItemClick(item)}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    border: 'none',
                    borderBottom: index < searchResults.length - 1 ? '1px solid #dee2e6' : 'none',
                    background: selectedItem?.code === item.code ? '#0d6efd' : 'white',
                    color: selectedItem?.code === item.code ? 'white' : 'black',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedItem?.code !== item.code) {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedItem?.code !== item.code) {
                      e.currentTarget.style.backgroundColor = 'white';
                    }
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{item.code}</div>
                    <div style={{ 
                      fontSize: '0.875rem',
                      color: selectedItem?.code === item.code ? 'white' : '#6c757d'
                    }}>
                      {item.name}
                    </div>
                  </div>
                  {selectedItem?.code === item.code && (
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      backgroundColor: 'white',
                      color: '#0d6efd',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      <CheckCircle size={14} />
                      Copied
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Empty States */}
          {!isSearching && searchResults.length === 0 && !error && (
            (activeTab === 'airports' && searchQuery.trim().length >= 3) ||
            (activeTab === 'airlines' && searchQuery.trim().length >= 2)
          ) && (
            <div style={{ textAlign: 'center', color: '#6c757d', padding: '3rem' }}>
              <Search size={48} style={{ opacity: 0.25, marginBottom: '1rem' }} />
              <p>No results found. Try a different search term.</p>
            </div>
          )}

          {!isSearching && searchResults.length === 0 && !error && (
            (activeTab === 'airports' && searchQuery.trim().length < 3) ||
            (activeTab === 'airlines' && searchQuery.trim().length < 2) ||
            !searchQuery.trim()
          ) && (
            <div style={{ textAlign: 'center', color: '#6c757d', padding: '3rem' }}>
              <Search size={48} style={{ opacity: 0.25, marginBottom: '1rem' }} />
              <p>
                {activeTab === 'airports' 
                  ? 'Enter at least 3 characters to search for airports'
                  : 'Enter at least 2 characters to search for airlines'}
              </p>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div style={{
          padding: '1rem 1.5rem',
          borderTop: '1px solid #dee2e6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <small style={{ color: '#6c757d' }}>
            Results appear automatically as you type. Modal stays open for multiple searches.
          </small>
          <button
            onClick={onHide}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #6c757d',
              borderRadius: '4px',
              backgroundColor: '#6c757d',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CodeLookupModal;